import { Category, PropertyOption } from '@/api/types/categories.types';

const isPropertyOption = (
  obj: Category | PropertyOption
): obj is PropertyOption => {
  return 'propertyLegacyId' in obj;
};

export const groupCategoriesByParentName = (
  categories: (Category | PropertyOption)[]
) => {
  const isDuplicate = (
    collection: (Category | PropertyOption)[],
    item: Category | PropertyOption
  ): boolean => {
    return collection.some(existingItem => {
      if (isPropertyOption(item)) {
        return (
          isPropertyOption(existingItem) &&
          item.legacyResourceId === existingItem.legacyResourceId
        );
      }
      return !isPropertyOption(existingItem) && item.id === existingItem.id;
    });
  };

  return categories.reduce((grouped, item) => {
    const parentName = isPropertyOption(item)
      ? item.parentCategory?.name
      : item.parent?.name;

    if (!parentName) return grouped;

    const currentGroup = grouped.get(parentName) ?? [];

    if (!isDuplicate(currentGroup, item)) {
      grouped.set(parentName, [...currentGroup, item]);
    }

    return grouped;
  }, new Map<string, (Category | PropertyOption)[]>());
};

export function findSubcategoryByName(
  query: string,
  categories?: Category[]
): (Category | PropertyOption)[] {
  if (!categories) return [];

  const matchingCategories: (Category | PropertyOption)[] = [];

  function searchInCategory(category: Category | PropertyOption) {
    if (category.name.toLowerCase().includes(query.toLowerCase())) {
      matchingCategories.push(category);
    }

    if ('children' in category) {
      category.children?.forEach(searchInCategory);
    }

    if ('propertyOptions' in category) {
      category.propertyOptions?.forEach(propertyOption =>
        searchInCategory({
          ...propertyOption,
          parentCategory: category,
        })
      );
    }
  }

  categories.forEach(searchInCategory);

  return matchingCategories;
}

export function findAndGroupCategoriesByParentName(
  query: string,
  categories?: Category[]
) {
  const matchingCategories = findSubcategoryByName(query, categories);
  return groupCategoriesByParentName(matchingCategories);
}
